import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaChevronRight, FaHome } from 'react-icons/fa';
import { Clock, Truck, Wallet } from 'lucide-react';
import { RatingStarsFa } from '../utils/ratingStars';
import { useRestaurantDetails } from '../hooks/useRestaurantDetails';
import { useCart } from '../contexts/CartContext';
import { useFavoriteAuth } from '../hooks/useFavoriteAuth';
import * as api from '../services/api';
import LoginPopup from '../components/LoginPopup';
import NavBar from '../components/nav';

// Import des composants
import {
  HeroSection,
  LoyaltyPoints,
  MenuTabs,
  ReviewsSection,
  SimilarRestaurants,
  DishDetailPopup,
  MenuDetailPopup,
  RestaurantImagesSection,
  RestaurantLocation,
  ReportModal
} from '../components/restaurant';

const PAGE_TAB_IDS = ['carte', 'avis', 'infos'];

const formatTimeSlot = (t) => {
  if (t == null || t === '') return '';
  const s = String(t);
  return s.length >= 5 ? s.slice(0, 5) : s;
};
const getApiDayOfWeek = () => {
  const js = new Date().getDay();
  return js === 0 ? 6 : js - 1;
};

const RestaurantDetails = () => {
  const { t } = useTranslation();
  const DAY_LABELS = t('restaurant.days', { returnObjects: true });
  const PAGE_TABS = PAGE_TAB_IDS.map((id) => ({ id, label: t(`restaurant.tabs.${id}`) }));
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('lunch');
  const [pageTab, setPageTab] = useState('carte');
  const [selectedDish, setSelectedDish] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportForm, setReportForm] = useState({
    reason: '',
    description: '',
    contact: ''
  });
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: ''
  });

  // Utiliser le hook personnalisé pour les données du restaurant avec l'ID
  const {
    restaurant,
    dishes,
    menus,
    reviews,
    categories,
    similarRestaurants,
    loading,
    error,
    unavailableDetail,
    getDishDetails,
    getMenuDetails,
    getDishesByCategory,
    getCategoryName,
    formatPrice,
    formatRating,
    formatDate,
    getImageUrl,
    refreshData
  } = useRestaurantDetails(id);

  // Utiliser le hook d'authentification pour les favoris
  const {
    isAuthenticated,
    showLoginPopup,
    handleFavoriteAction,
    handleReviewAction,
    handleLoginSuccess,
    closeLoginPopup
  } = useFavoriteAuth();

  // Utiliser le contexte du panier
  const { addToCart } = useCart();

  // Ouvrir automatiquement le menu ciblé si ?menu= est présent dans l'URL
  useEffect(() => {
    const menuIdParam = searchParams.get('menu');
    if (!menuIdParam || !menus || menus.length === 0) return;
    const targetMenuId = parseInt(menuIdParam, 10);
    const targetMenu = menus.find(m => m.id === targetMenuId);
    if (targetMenu) {
      setSelectedMenu(targetMenu);
    }
  }, [searchParams, menus]);

  // Gérer le clic sur un plat
  const handleDishClick = async (dishId) => {
    try {
      const dishDetails = await getDishDetails(dishId);
      if (dishDetails) {
        setSelectedDish(dishDetails);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails du plat:', error);
    }
  };

  // Gérer le clic sur un menu (détail)
  const handleMenuClick = (menu) => {
    setSelectedMenu(menu);
  };

  const closeMenuPopup = () => {
    setSelectedMenu(null);
  };

  // Gérer l'ajout au panier
  const handleAddToCart = (dish) => {
    // Utiliser la quantité passée depuis le popup, ou 1 par défaut
    const quantity = dish.quantity || 1;
    
    // S'assurer que restaurant existe avant d'ajouter au panier
    if (!restaurant && !dish.restaurant_id) {
      console.error('Restaurant non disponible pour ajouter au panier');
      alert(t('restaurant.errors.addToCartNoRestaurant'));
      return;
    }
    
    try {
      addToCart({
        id: dish.id,
        name: dish.name,
        price: dish.price,
        image: dish.dish_image_url || dish.image_url,
        restaurant_id: restaurant?.id || dish.restaurant_id || parseInt(id),
        restaurant_name: restaurant?.name || dish.restaurant_name || t('restaurant.fallbackName'),
        quantity: quantity
      });

      // Afficher une notification de succès (optionnel)
      console.log('Plat ajouté au panier:', dish.name, 'Quantité:', quantity);
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      alert(t('restaurant.errors.addToCartFailed'));
    }
  };

  // Ouvrir le formulaire d'avis (vérifier la connexion)
  const handleOpenReviewForm = () => {
    if (!isAuthenticated) {
      handleReviewAction('openReviewForm', () => setShowReviewForm(true));
      return;
    }
    setShowReviewForm(true);
  };

  // Soumettre l'avis via l'API
  const handleReviewSubmit = async (formData) => {
    if (!restaurant?.id) return;
    if (!isAuthenticated) {
      handleReviewAction('openReviewForm', () => setShowReviewForm(true));
      return;
    }
    try {
      await api.createRestaurantReview(restaurant.id, formData);
      setReviewForm({ rating: 5, comment: '' });
      setShowReviewForm(false);
      refreshData();
    } catch (error) {
      console.error('Erreur lors de la création de l\'avis:', error);
      let msg = error?.message;
      if (typeof msg !== 'string' || !msg) msg = t('restaurant.errors.reviewFailed');
      alert(msg);
    }
  };

  // Gérer le clic sur les favoris (si non connecté, handleFavoriteAction affiche le popup de connexion)
  const handleFavoriteClick = async () => {
    const action = restaurant.is_favorite ? 'remove' : 'add';
    const success = await handleFavoriteAction(action, restaurant.id, 'restaurant');
    if (success) refreshData();
  };

  const openReportModal = async () => {
    if (!isAuthenticated) {
      await handleReviewAction('openReportForm', () => setShowReportModal(true));
      return;
    }
    setShowReportModal(true);
  };

  const closeReportModal = () => {
    if (reportSubmitting) return;
    setShowReportModal(false);
    setReportForm({ reason: '', description: '', contact: '' });
  };

  const handleReportSubmit = async (event) => {
    event.preventDefault();
    if (!restaurant?.id) return;
    if (!isAuthenticated) {
      await handleReviewAction('openReportForm', () => setShowReportModal(true));
      return;
    }

    try {
      setReportSubmitting(true);
      await api.createComplaint({
        restaurant_id: Number(restaurant.id),
        reason: reportForm.reason,
        message: reportForm.description,
        contact: reportForm.contact || undefined,
      });
      alert(t('restaurant.reportSent'));
      setShowReportModal(false);
      setReportForm({ reason: '', description: '', contact: '' });
    } catch (error) {
      console.error('Erreur lors de l\'envoi du signalement:', error);
      alert(error?.message || t('restaurant.errors.reportFailed'));
    } finally {
      setReportSubmitting(false);
    }
  };

  // Fermer le popup de détails du plat
  const closeDishPopup = () => {
    setSelectedDish(null);
  };

  const renderStars = (rating) => <RatingStarsFa rating={rating} size="md" />;

  // Déterminer l'URL de la bannière
  const bannerImageUrl = getImageUrl(restaurant?.banner_image);

  // Plat le plus mis en avant (meilleure note, sinon premier de la carte)
  const topDish = useMemo(() => {
    if (!dishes || dishes.length === 0) return null;
    return [...dishes].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0];
  }, [dishes]);

  const sortedHours = useMemo(() => {
    const list = restaurant?.hours;
    if (!Array.isArray(list) || list.length === 0) return [];
    return [...list].sort((a, b) => (a.day_of_week ?? 0) - (b.day_of_week ?? 0));
  }, [restaurant?.hours]);
  const todayApi = getApiDayOfWeek();

  // Afficher le loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <div className="animate-pulse">
          {/* Hero banner */}
          <div className="w-full h-56 sm:h-72 bg-gray-200" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* Restaurant header */}
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-xl bg-gray-200 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-7 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
            {/* Tab bar */}
            <div className="flex gap-4 border-b border-gray-200 pb-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-8 w-20 bg-gray-200 rounded" />
              ))}
            </div>
            {/* Dish grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden border border-gray-100">
                  <div className="h-36 bg-gray-200" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Restaurant suspendu / retiré du catalogue public
  if (unavailableDetail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <div className="w-full max-w-lg mx-auto px-4 py-16 text-center">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <p className="text-amber-800 font-semibold text-lg mb-2">{t('restaurant.unavailable')}</p>
            <p className="text-gray-700 mb-4">{unavailableDetail.message}</p>
            {unavailableDetail.suspensionNote ? (
              <p className="text-sm text-gray-900 font-normal text-left bg-white/80 rounded-lg p-4 border border-amber-100 mb-6">
                <span className="font-medium text-gray-800">{t('restaurant.information')} </span>
                {unavailableDetail.suspensionNote}
              </p>
            ) : null}
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-lg text-white font-medium"
              style={{ backgroundColor: 'rgb(5, 70, 37)' }}
            >
              {t('restaurant.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Afficher l'erreur
  if (error) {
    return (
      <div className="w-full pl-6 max-w-7xl mx-auto px-4 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('restaurant.error')}</h2>
          <p className="text-gray-900 font-normal mb-4">{error}</p>
          <button
            onClick={refreshData}
            className="px-4 py-2 text-white rounded-lg transition-colors"
            style={{ backgroundColor: 'rgb(5, 70, 37)', '&:hover': { backgroundColor: 'rgb(0, 53, 21)' } }}
          >
            {t('restaurant.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Si pas de restaurant, ne rien afficher
  if (!restaurant) {
    return null;
  }

  return (
    <div className="min-h-screen bg-namke-white">
      {/* Navbar */}
      <NavBar />

      <div className="w-full pl-6 max-w-7xl mx-auto px-4 bg-namke-white">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center space-x-2 text-sm text-namke-black/70 font-normal mb-6 pt-5">
        <Link to="/" className="flex items-center transition-colors" style={{ '&:hover': { color: 'rgb(5, 70, 37)' } }}>
          <FaHome className="w-4 h-4 mr-1" />
          {t('nav.home')}
        </Link>
        <FaChevronRight className="w-3 h-3" />
        <Link to="/" className="transition-colors" style={{ '&:hover': { color: 'rgb(5, 70, 37)' } }}>
          {t('nav.restaurants')}
        </Link>
        <FaChevronRight className="w-3 h-3" />
        <span className="text-namke-black font-medium">{restaurant.name}</span>
      </nav>

      {/* Hero Section avec données du restaurant */}
      <HeroSection 
        restaurant={restaurant}
        bannerImageUrl={bannerImageUrl}
        renderStars={renderStars}
        formatRating={formatRating}
        isFavorite={restaurant.is_favorite}
        onFavoriteClick={handleFavoriteClick}
      />

      <div className="flex justify-end mb-4">
        <button
          onClick={openReportModal}
          className="px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
        >
          {t('restaurant.report')}
        </button>
      </div>

      {/* Loyalty Points */}
      <LoyaltyPoints />

      {/* Chiffres clés */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: '12px', marginBottom: 'clamp(28px, 4vw, 40px)' }}>
        <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span className="flex items-center gap-1.5" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>
            <Clock size={12} /> {t('restaurant.readyIn')}
          </span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{restaurant.delivery_time || '30-45 min'}</strong>
        </div>
        <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span className="flex items-center gap-1.5" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>
            <Truck size={12} /> {t('restaurant.delivery')}
          </span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{formatPrice ? formatPrice(restaurant.delivery_fee || 0) : `${restaurant.delivery_fee || 0}€`}</strong>
        </div>
        <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span className="flex items-center gap-1.5" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>
            <Wallet size={12} /> {t('restaurant.minimum')}
          </span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{formatPrice ? formatPrice(restaurant.minimum_order || 0) : `${restaurant.minimum_order || 0}€`}</strong>
        </div>
      </div>

      {/* Onglets — Carte / Avis / Infos pratiques */}
      <div style={{ display: 'flex', gap: 'clamp(18px, 3vw, 36px)', borderBottom: '1px solid rgba(5,16,4,0.12)', marginBottom: 'clamp(24px, 3vw, 34px)', overflowX: 'auto' }}>
        {PAGE_TABS.map((tabDef) => (
          <button
            key={tabDef.id}
            type="button"
            onClick={() => setPageTab(tabDef.id)}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontFamily: 'Poppins, sans-serif', fontSize: '16px', padding: '12px 2px 14px',
              borderBottom: '3px solid transparent', whiteSpace: 'nowrap',
              color: pageTab === tabDef.id ? '#054625' : 'rgba(5,16,4,0.6)',
              fontWeight: pageTab === tabDef.id ? 600 : 400,
              borderBottomColor: pageTab === tabDef.id ? '#EF4C23' : 'transparent',
            }}
          >
            {tabDef.label}
          </button>
        ))}
      </div>

      <div
        className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px]"
        style={{ gap: 'clamp(22px, 3vw, 40px)', alignItems: 'start', marginBottom: 'clamp(40px, 5vw, 64px)' }}
      >
        <div style={{ minWidth: 0 }}>
          {pageTab === 'carte' && (
            <MenuTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              handleDishClick={handleDishClick}
              handleMenuClick={handleMenuClick}
              dishes={dishes}
              menus={menus}
              categories={categories}
              getDishesByCategory={getDishesByCategory}
              getCategoryName={getCategoryName}
              formatPrice={formatPrice}
              getImageUrl={getImageUrl}
              handleAddToCart={handleAddToCart}
            />
          )}

          {pageTab === 'avis' && (
            <ReviewsSection
              reviews={reviews}
              renderStars={renderStars}
              formatDate={formatDate}
              showReviewForm={showReviewForm}
              setShowReviewForm={setShowReviewForm}
              reviewForm={reviewForm}
              setReviewForm={setReviewForm}
              onReviewSubmit={handleReviewSubmit}
              onOpenReviewForm={handleOpenReviewForm}
            />
          )}

          {pageTab === 'infos' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(24px, 3vw, 34px)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: '16px' }}>
                <div style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{t('restaurant.hours')}</h3>
                  {sortedHours.length > 0 ? (
                    sortedHours.map((h, idx) => {
                      const dow = h.day_of_week ?? 0;
                      const isToday = dow === todayApi;
                      const label = DAY_LABELS[dow] ?? t('restaurant.dayFallback', { day: dow });
                      const line = (!h.is_closed && h.open_time && h.close_time)
                        ? `${formatTimeSlot(h.open_time)} – ${formatTimeSlot(h.close_time)}`
                        : t('restaurant.closed');
                      return (
                        <span key={`${dow}-${idx}`} className="flex justify-between" style={{ fontSize: '14.5px', fontWeight: isToday ? 600 : 400, color: isToday ? '#054625' : '#051004' }}>
                          {label}{isToday ? ` (${t('restaurant.today')})` : ''}
                          <span>{line}</span>
                        </span>
                      );
                    })
                  ) : restaurant.opening_hours ? (
                    <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.7)', whiteSpace: 'pre-line' }}>{restaurant.opening_hours}</p>
                  ) : (
                    <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>{t('restaurant.noHours')}</p>
                  )}
                </div>

                <div style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{t('restaurant.delivery')}</h3>
                  <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.6, color: 'rgba(5,16,4,0.78)' }}>
                    {t('restaurant.deliveryTo', { address: restaurant.address, city: restaurant.city ? `, ${restaurant.city}` : '' })}
                  </p>
                  <div className="flex flex-wrap gap-2" style={{ marginTop: '4px' }}>
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', background: '#FBF9D9', padding: '7px 15px', borderRadius: '999px' }}>
                      {t('restaurant.delivery')} {formatPrice ? formatPrice(restaurant.delivery_fee || 0) : `${restaurant.delivery_fee || 0}€`}
                    </span>
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', background: '#FBF9D9', padding: '7px 15px', borderRadius: '999px' }}>
                      {t('restaurant.minimum')} {formatPrice ? formatPrice(restaurant.minimum_order || 0) : `${restaurant.minimum_order || 0}€`}
                    </span>
                  </div>
                </div>

                <div style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{t('restaurant.theRestaurant')}</h3>
                  <span className="flex items-center gap-2.5" style={{ fontSize: '14.5px' }}>
                    <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, color: '#051004', flexShrink: 0 }}>✓</span>
                    {t('restaurant.activeOnNamke')}
                  </span>
                  {restaurant.cuisine_type && (
                    <span className="flex items-center gap-2.5" style={{ fontSize: '14.5px' }}>
                      <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, color: '#051004', flexShrink: 0 }}>✓</span>
                      {t('restaurant.cuisineLabel', { cuisine: restaurant.cuisine_type })}
                    </span>
                  )}
                  {restaurant.phone && (
                    <span className="flex items-center gap-2.5" style={{ fontSize: '14.5px' }}>
                      <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, color: '#051004', flexShrink: 0 }}>✓</span>
                      {restaurant.phone}
                    </span>
                  )}
                  {restaurant.website && (
                    <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5" style={{ fontSize: '14.5px', color: '#054625', fontWeight: 600 }}>
                      <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, color: '#051004', flexShrink: 0 }}>✓</span>
                      {t('restaurant.website')}
                    </a>
                  )}
                </div>
              </div>

              <RestaurantLocation restaurant={restaurant} />
              <RestaurantImagesSection restaurant={restaurant} getImageUrl={getImageUrl} />
            </div>
          )}
        </div>

        {/* Aside — plat vedette */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
          {topDish && (
            <div style={{ background: '#054625', color: '#FBF9D9', borderRadius: '24px', padding: '26px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFC717' }}>{t('restaurant.mostOrdered')}</span>
              <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                {topDish.name} · {formatPrice ? formatPrice(topDish.price) : `${topDish.price}€`}
              </strong>
              {topDish.description && (
                <span style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'rgba(251,249,217,0.82)' }}>{topDish.description}</span>
              )}
              <button
                type="button"
                onClick={() => handleDishClick(topDish.id)}
                style={{ background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', padding: '14px 26px', borderRadius: '999px', textAlign: 'center', marginTop: '4px', border: 'none', cursor: 'pointer' }}
              >
                {t('restaurant.viewDish')}
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Similar Restaurants */}
      <SimilarRestaurants
        restaurants={similarRestaurants}
        currentRestaurantId={restaurant?.id}
      />

      {/* Dish Detail Popup */}
      <DishDetailPopup 
        selectedDish={selectedDish}
        closeDishPopup={closeDishPopup}
        renderStars={renderStars}
        formatPrice={formatPrice}
        formatDate={formatDate}
        handleAddToCart={handleAddToCart}
      />

      {/* Menu Detail Popup */}
      <MenuDetailPopup
        selectedMenu={selectedMenu}
        closeMenuPopup={closeMenuPopup}
        renderStars={renderStars}
        formatPrice={formatPrice}
        getImageUrl={getImageUrl}
        handleAddToCart={(menu) => restaurant && addToCart({
          id: menu.id,
          name: menu.name,
          price: menu.price || 0,
          image: menu.image_url || menu.dish_image_url,
          restaurant_id: restaurant.id,
          restaurant_name: restaurant.name,
          quantity: 1
        })}
      />

      {/* Login Popup */}
      <LoginPopup 
        isOpen={showLoginPopup}
        onClose={closeLoginPopup}
        onLoginSuccess={handleLoginSuccess}
      />

      <ReportModal
        isOpen={showReportModal}
        onClose={closeReportModal}
        reportForm={reportForm}
        setReportForm={setReportForm}
        reportSubmitting={reportSubmitting}
        onSubmit={handleReportSubmit}
      />

      </div>
    </div>
  );
};

export default RestaurantDetails;
